// admin-panel/src/pages/AuthorsList.js
import React, { useEffect, useState } from "react";
import {
  Table, Button, Avatar, Typography, Space, Card, Modal,
  Form, Input, message, Popconfirm, Row, Col, Tag, Grid,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UserOutlined,
} from "@ant-design/icons";
import { getAllAuthors, createAuthor, updateAuthor, deleteAuthor } from "../utilscopy/firebase";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

export default function AuthorsList() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  const loadAuthors = async () => {
    setLoading(true);
    try {
      const data = await getAllAuthors();
      setAuthors(data);
    } catch (e) {
      message.error("Failed to load authors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAuthors(); }, []);

  const openNew = () => {
    setEditingAuthor(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (author) => {
    setEditingAuthor(author);
    form.setFieldsValue(author);
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editingAuthor) {
        await updateAuthor(editingAuthor.id, values);
        message.success("Author updated!");
        setAuthors((prev) =>
          prev.map((a) => (a.id === editingAuthor.id ? { ...a, ...values } : a))
        );
      } else {
        const newAuthor = await createAuthor(values);
        message.success("Author created!");
        setAuthors((prev) => [...prev, newAuthor]);
      }
      setModalOpen(false);
      form.resetFields();
    } catch (e) {
      console.error("Author save failed:", e);
      message.error("Save failed: " + (e.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAuthor(id);
      message.success("Author deleted");
      setAuthors((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      message.error("Delete failed");
    }
  };

  const columns = [
    {
      title: "Author",
      key: "author",
      render: (_, record) => (
        <Space>
          <Avatar src={record.avatar} size={44} icon={<UserOutlined />} style={{ border: "2px solid #f0f0f0" }} />
          <div>
            <Text strong style={{ display: "block" }}>{record.name}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.role}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Tag color="blue" style={{ borderRadius: 20 }}>{role}</Tag>
      ),
      width: 160,
    },
    {
      title: "Bio",
      dataIndex: "bio",
      key: "bio",
      render: (bio) => (
        <Text type="secondary" style={{ fontSize: 13 }} ellipsis>
          {bio}
        </Text>
      ),
      ellipsis: true,
    },
    {
      title: "Avatar URL",
      dataIndex: "avatar",
      key: "avatar",
      render: (url) => (
        <Text type="secondary" style={{ fontSize: 11, fontFamily: "monospace" }} ellipsis>
          {url}
        </Text>
      ),
      width: 200,
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
            style={{ borderColor: "#e2e8f0", borderRadius: 8 }}
          />
          <Popconfirm
            title="Delete author?"
            description="Posts by this author will lose their author reference."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ borderRadius: 8 }}
            />
          </Popconfirm>
        </Space>
      ),
      width: 100,
    },
  ];

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 12 : 0,
          marginBottom: 20,
        }}
      >
        <Text type="secondary">{authors.length} author{authors.length !== 1 ? "s" : ""}</Text>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openNew}
          size="large"
          block={isMobile}
          style={{ borderRadius: 10, background: "#111", borderColor: "#111", fontWeight: 600 }}
        >
          New Author
        </Button>
      </div>

      {/* Author Cards (mobile-friendly) */}
      <Row gutter={isMobile ? [12, 12] : [16, 16]} style={{ marginBottom: 24 }}>
        {authors.map((author) => (
          <Col key={author.id} xs={24} sm={12} md={8}>
            <Card
              style={{ borderRadius: 16, border: "1px solid #f0f0f0" }}
              bodyStyle={{ padding: isMobile ? 16 : 24 }}
              actions={[
                <Button
                  key="edit"
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => openEdit(author)}
                >
                  Edit
                </Button>,
                <Popconfirm
                  key="delete"
                  title="Delete author?"
                  onConfirm={() => handleDelete(author.id)}
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>,
              ]}
            >
              <div style={{ textAlign: "center" }}>
                <Avatar
                  src={author.avatar}
                  size={72}
                  icon={<UserOutlined />}
                  style={{ border: "3px solid #f0f0f0", marginBottom: 12 }}
                />
                <Title level={5} style={{ margin: "0 0 4px" }}>{author.name}</Title>
                <Tag color="blue" style={{ borderRadius: 20, marginBottom: 10 }}>{author.role}</Tag>
                <Text type="secondary" style={{ fontSize: 13, display: "block", lineHeight: 1.5 }}>
                  {author.bio}
                </Text>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create / Edit Modal */}
      <Modal
        title={
          <Title level={5} style={{ margin: 0 }}>
            {editingAuthor ? "Edit Author" : "New Author"}
          </Title>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        width={isMobile ? "92%" : 520}
        centered={isMobile}
        style={{ borderRadius: 16 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input size="large" placeholder="e.g. Naveen Kumar" style={{ borderRadius: 10 }} />
          </Form.Item>

          <Form.Item
            name="role"
            label="Role / Title"
            rules={[{ required: true, message: "Role is required" }]}
          >
            <Input placeholder="e.g. Editor in Chief" style={{ borderRadius: 10 }} />
          </Form.Item>

          <Form.Item name="avatar" label="Avatar URL">
            <Input
              placeholder="https://api.dicebear.com/7.x/notionists/svg?seed=name"
              style={{ borderRadius: 10 }}
            />
          </Form.Item>

          <Form.Item name="bio" label="Bio">
            <TextArea
              rows={3}
              placeholder="A short bio shown on posts..."
              style={{ borderRadius: 10 }}
              maxLength={200}
              showCount
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: isMobile ? "stretch" : "flex-end",
              flexDirection: isMobile ? "column-reverse" : "row",
            }}
          >
            <Button block={isMobile} onClick={() => { setModalOpen(false); form.resetFields(); }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving}
              block={isMobile}
              style={{ background: "#111", borderColor: "#111", fontWeight: 600, borderRadius: 8 }}
            >
              {editingAuthor ? "Update" : "Create Author"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}